import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Bold, Italic, Heading, Link as LinkIcon, List, Code, Quote, Eye, EyeOff, BookOpen } from 'lucide-react';
import { EditorState, type Extension } from '@codemirror/state';
import { EditorView, keymap, placeholder as cmPlaceholder, ViewUpdate } from '@codemirror/view';
import { markdown, markdownLanguage } from '@codemirror/lang-markdown';
import { languages } from '@codemirror/language-data';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { closeBrackets, closeBracketsKeymap } from '@codemirror/autocomplete';
import { searchKeymap, highlightSelectionMatches } from '@codemirror/search';
import { syntaxHighlighting, defaultHighlightStyle, bracketMatching } from '@codemirror/language';
import MarkdownContent from '../ui/MarkdownContent';
import type { CampaignData } from '../../../shared/types';
import { useWikiLinkAutocomplete, WikiLinkDropdown } from '../wiki/WikiLinkAutocomplete';

// ── Theme ────────────────────────────────────────────────────────────

function buildEditorTheme(isDark: boolean) {
  return EditorView.theme({
    '&': {
      fontFamily: 'var(--font-body, "Inter", sans-serif)',
      fontSize: '14px',
      color: 'var(--text)',
      backgroundColor: 'var(--bg-input, var(--bg-surface))',
      borderRadius: '0.5rem',
      border: '1px solid var(--border)',
    },
    '.cm-content': {
      padding: '12px 16px',
      minHeight: '120px',
      caretColor: 'var(--accent)',
    },
    '&.cm-focused': {
      outline: 'none',
      boxShadow: '0 0 0 1px var(--accent)',
    },
    '.cm-gutters': {
      display: 'none',
    },
    '.cm-cursor': {
      borderLeftColor: 'var(--accent)',
    },
    '.cm-selectionBackground': {
      backgroundColor: isDark ? 'rgba(127, 109, 242, 0.3) !important' : 'rgba(127, 109, 242, 0.15) !important',
    },
    '&.cm-focused .cm-selectionBackground': {
      backgroundColor: isDark ? 'rgba(127, 109, 242, 0.4) !important' : 'rgba(127, 109, 242, 0.2) !important',
    },
    '.cm-activeLine': {
      backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
    },
    '.cm-scroller': {
      overflow: 'auto',
    },
    // Wiki link styling
    '.cm-wikilink': {
      color: 'var(--accent-text)',
      fontWeight: '500',
    },
  }, { dark: isDark });
}

// ── Wiki link decoration ─────────────────────────────────────────────

import { Decoration, type DecorationSet, ViewPlugin, type PluginValue } from '@codemirror/view';

const wikiLinkMark = Decoration.mark({ class: 'cm-wikilink' });

function buildWikiLinkDecorations(view: EditorView): DecorationSet {
  const builder: { from: number; to: number }[] = [];
  for (const { from, to } of view.visibleRanges) {
    const text = view.state.doc.sliceString(from, to);
    const regex = /\[\[.*?\]\]/g;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(text)) !== null) {
      builder.push({ from: from + match.index, to: from + match.index + match[0].length });
    }
  }
  return Decoration.set(
    builder.map(b => wikiLinkMark.range(b.from, b.to)),
  );
}

const wikiLinkPlugin = ViewPlugin.fromClass(
  class implements PluginValue {
    decorations: DecorationSet;
    constructor(view: EditorView) {
      this.decorations = buildWikiLinkDecorations(view);
    }
    update(update: ViewUpdate) {
      if (update.docChanged || update.viewportChanged) {
        this.decorations = buildWikiLinkDecorations(update.view);
      }
    }
  },
  { decorations: (v) => v.decorations },
);

// ── Main Component ───────────────────────────────────────────────────

type MarkdownEditorProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: string;
  campaignData?: CampaignData;
  knownWikiTitles?: Set<string>;
};

export default function MarkdownEditor({
  value,
  onChange,
  placeholder = 'Write markdown here...',
  minHeight = '200px',
  campaignData,
}: MarkdownEditorProps) {
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [showPreview, setShowPreview] = useState(false);

  // Detect dark mode
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.getAttribute('data-theme') === 'dark',
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.getAttribute('data-theme') === 'dark');
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  // Fake textarea ref for autocomplete (we wire into the underlying CM textarea)
  const fakeTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  const emptyCampaignData: CampaignData = useMemo(() => ({
    name: '', setting: '', sessionNumber: 0,
    playerCharacters: [], npcs: [], locations: [], organizations: [],
    plotThreads: [], sessionNotes: [], calendars: [], calendarEvents: [],
    maps: [], mapLayers: [], mapMarkers: [], timelines: [], timelineEvents: [],
    futureContent: [], wikiArticles: [],
  }), []);

  const effectiveCampaignData = campaignData || emptyCampaignData;

  const knownWikiTitles = useMemo(
    () => new Set((effectiveCampaignData.wikiArticles || []).map(a => a.title.toLowerCase())),
    [effectiveCampaignData.wikiArticles]
  );

  const autocomplete = useWikiLinkAutocomplete(
    effectiveCampaignData,
    fakeTextareaRef,
    value,
    onChange,
  );

  // Create/destroy EditorView
  useEffect(() => {
    if (!editorContainerRef.current) return;

    const extensions: Extension[] = [
      history(),
      closeBrackets(),
      bracketMatching(),
      highlightSelectionMatches(),
      syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
      markdown({ base: markdownLanguage, codeLanguages: languages }),
      wikiLinkPlugin,
      buildEditorTheme(isDark),
      cmPlaceholder(placeholder),
      EditorView.lineWrapping,
      keymap.of([
        ...defaultKeymap,
        ...historyKeymap,
        ...closeBracketsKeymap,
        ...searchKeymap,
      ]),
      EditorView.updateListener.of((update: ViewUpdate) => {
        if (update.docChanged) {
          onChangeRef.current(update.state.doc.toString());
        }
      }),
    ];

    const state = EditorState.create({
      doc: value,
      extensions,
    });

    const view = new EditorView({
      state,
      parent: editorContainerRef.current,
    });

    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // Re-create editor only when dark mode changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDark, placeholder]);

  // Sync external value changes into editor (e.g., AI draft fill)
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const currentDoc = view.state.doc.toString();
    if (currentDoc !== value) {
      view.dispatch({
        changes: { from: 0, to: currentDoc.length, insert: value },
      });
    }
  }, [value]);

  // Toolbar actions
  const wrapSelection = useCallback((before: string, after: string) => {
    const view = viewRef.current;
    if (!view) return;
    const { from, to } = view.state.selection.main;
    const selected = view.state.doc.sliceString(from, to);
    view.dispatch({
      changes: { from, to, insert: `${before}${selected}${after}` },
      selection: { anchor: from + before.length, head: to + before.length },
    });
    view.focus();
  }, []);

  const insertAtCursor = useCallback((text: string) => {
    const view = viewRef.current;
    if (!view) return;
    const { from } = view.state.selection.main;
    view.dispatch({
      changes: { from, to: from, insert: text },
      selection: { anchor: from + text.length },
    });
    view.focus();
  }, []);

  const prependLine = useCallback((prefix: string) => {
    const view = viewRef.current;
    if (!view) return;
    const { from } = view.state.selection.main;
    const line = view.state.doc.lineAt(from);
    view.dispatch({
      changes: { from: line.from, to: line.from, insert: prefix },
    });
    view.focus();
  }, []);

  const toolbarButtons = [
    { icon: Bold, label: 'Bold (Cmd+B)', action: () => wrapSelection('**', '**') },
    { icon: Italic, label: 'Italic (Cmd+I)', action: () => wrapSelection('*', '*') },
    { icon: Heading, label: 'Heading', action: () => prependLine('## ') },
    { icon: LinkIcon, label: 'Link (Cmd+K)', action: () => wrapSelection('[', '](url)') },
    { icon: BookOpen, label: 'Wiki Link', action: () => insertAtCursor('[[') },
    { icon: List, label: 'List', action: () => prependLine('- ') },
    { icon: Code, label: 'Code', action: () => wrapSelection('`', '`') },
    { icon: Quote, label: 'Quote', action: () => prependLine('> ') },
  ];

  return (
    <div className="field-block">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-1 py-1 border border-[var(--border)] border-b-0 rounded-t-lg bg-[var(--bg-surface)]">
        {toolbarButtons.map(({ icon: Icon, label, action }) => (
          <button
            key={label}
            type="button"
            onClick={action}
            className="p-1.5 rounded hover:bg-[var(--bg-surface-alt)] text-[var(--text-muted)] hover:text-[var(--text)] transition-colors"
            title={label}
          >
            <Icon size={14} />
          </button>
        ))}
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={() => setShowPreview(p => !p)}
            className={`p-1.5 rounded transition-colors ${
              showPreview
                ? 'bg-[var(--accent-muted)] text-[var(--accent-text)]'
                : 'hover:bg-[var(--bg-surface-alt)] text-[var(--text-muted)] hover:text-[var(--text)]'
            }`}
            title={showPreview ? 'Hide preview' : 'Show preview'}
          >
            {showPreview ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </div>
      </div>

      {/* Editor + Preview */}
      <div className={showPreview ? 'grid grid-cols-2 gap-0 border border-[var(--border)] rounded-b-lg overflow-hidden' : ''}>
        <div
          ref={editorContainerRef}
          style={{ minHeight }}
          className={showPreview ? 'border-r border-[var(--border)]' : ''}
        />
        {showPreview && (
          <div
            className="p-4 overflow-auto bg-[var(--bg-surface)]"
            style={{ minHeight }}
          >
            <MarkdownContent content={value} knownWikiTitles={knownWikiTitles} />
          </div>
        )}
      </div>

      {/* Autocomplete dropdown */}
      {autocomplete.autocompleteState.isOpen && (
        <WikiLinkDropdown
          suggestions={autocomplete.filtered}
          selectedIndex={autocomplete.autocompleteState.selectedIndex}
          position={autocomplete.dropdownPos}
          onSelect={autocomplete.insertLink}
          onClose={autocomplete.close}
        />
      )}
    </div>
  );
}
